import { useState } from 'react';
import { caddieAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from 'sonner';

function RegisterCaddieDialog({ open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    caddie_name: '',
    caddie_surname: '',
    caddie_phone: '',
    caddie_email: '',
    notes: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [addingExistingId, setAddingExistingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetDialogState = () => {
    setFormData({
      caddie_name: '',
      caddie_surname: '',
      caddie_phone: '',
      caddie_email: '',
      notes: ''
    });
    setSearchQuery('');
    setSearchResults([]);
    setAddingExistingId(null);
  };
 
  const handleSearchExisting = async () => {
    if (searchQuery.trim().length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }

    setSearching(true);
    try {
      const response = await caddieAPI.searchCaddies(searchQuery.trim());
      setSearchResults(response.data.caddies || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddExisting = async (caddie) => {
    setAddingExistingId(caddie.id);
    try {
      await caddieAPI.addExistingCaddie({
        caddie_user_id: caddie.id,
        notes: null
      });

      toast.success(`${caddie.full_name} added to your caddie list`);
      resetDialogState();
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add caddie');
    } finally {
      setAddingExistingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        caddie_name: formData.caddie_name,
        caddie_surname: formData.caddie_surname,
        caddie_phone: formData.caddie_phone,
        caddie_email: formData.caddie_email || null,
        notes: formData.notes || null
      };

      await caddieAPI.registerCaddie(payload);
      toast.success(`${formData.caddie_name} ${formData.caddie_surname} registered successfully!`);
      
      // Reset form
      resetDialogState();
 
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Registration failed';

      if (detail.includes('Please use Find Existing Caddie by Phone')) {
        try {
          setSearching(true);
          setSearchQuery(formData.caddie_phone);

          const response = await caddieAPI.searchCaddies(formData.caddie_phone.trim());
          setSearchResults(response.data.caddies || []);

          toast.error(detail);
        } catch (searchError) {
          toast.error(detail);
        } finally {
          setSearching(false);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetDialogState();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="register-caddie-dialog">
        <DialogHeader>
          <DialogTitle>Register a Caddie</DialogTitle>
          <DialogDescription>
            Add your preferred caddie for quick access when sending money
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
            <div className="space-y-2">
              <Label htmlFor="search-caddie">Find Existing Caddie by Phone</Label>
              <div className="flex gap-2">
                <Input
                  id="search-caddie"
                  type="text"
                  placeholder="0843002325 or +27843002325"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field"
                  data-testid="search-existing-caddie-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSearchExisting}
                  disabled={searching}
                  data-testid="search-existing-caddie-btn"
                >
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Search for a caddie who already has a CaddiePay account, then add them to your list.
              </p>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-2" data-testid="existing-caddie-results">
                {searchResults.map((caddie) => (
                  <Card key={caddie.id} className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{caddie.full_name}</p>
                        <p className="text-sm text-slate-600">{caddie.phone_number}</p>
                        {caddie.email && (
                          <p className="text-sm text-slate-500">{caddie.email}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={() => handleAddExisting(caddie)}
                        disabled={addingExistingId === caddie.id}
                        className="btn-primary"
                        data-testid={`add-existing-caddie-${caddie.id}`}
                      >
                        {addingExistingId === caddie.id ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="no-search-results">
                No matching existing caddies found. You can register one manually below.
              </p>
            )}
          </div>

          <div className="border-t pt-4">
            <p className="text-sm font-medium text-slate-700 mb-4">Or register manually</p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="caddie_name">First Name *</Label>
                <Input
                  id="caddie_name"
                  type="text"
                  placeholder="John"
                  value={formData.caddie_name}
                  onChange={(e) => setFormData({ ...formData, caddie_name: e.target.value })}
                  required
                  className="input-field"
                  data-testid="caddie-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="caddie_surname">Last Name *</Label>
                <Input
                  id="caddie_surname"
                  type="text"
                  placeholder="Doe"
                  value={formData.caddie_surname}
                  onChange={(e) => setFormData({ ...formData, caddie_surname: e.target.value })}
                  required
                  className="input-field"
                  data-testid="caddie-surname-input"
                />
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="caddie_phone">Phone Number *</Label>
              <Input
                id="caddie_phone"
                type="tel"
                placeholder="+27821234567"
                value={formData.caddie_phone}
                onChange={(e) => setFormData({ ...formData, caddie_phone: e.target.value })}
                required
                className="input-field"
                data-testid="caddie-phone-input"
              />
              <p className="text-xs text-slate-500">South African format: +27...</p>
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="caddie_email">Email (optional)</Label>
              <Input
                id="caddie_email"
                type="email"
                placeholder="john.doe@example.com"
                value={formData.caddie_email}
                onChange={(e) => setFormData({ ...formData, caddie_email: e.target.value })}
                className="input-field"
                data-testid="caddie-email-input"
              />
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="e.g., Favorite caddie, excellent service..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-field min-h-20"
                data-testid="caddie-notes-input"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              data-testid="cancel-register-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 btn-primary"
              disabled={loading}
              data-testid="submit-register-btn"
            >
              {loading ? 'Registering...' : 'Register Caddie'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RegisterCaddieDialog;
